import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * Modern Patient Records Service
 * 
 * Comprehensive service for patient record management with advanced features:
 * - Search and filtering
 * - Medical records management
 * - Document handling
 * - Audit trail access
 * - Staff assignments
 * - Care status management
 */

class PatientRecordsService {
    constructor() {
        this.baseUrl = '/patients';
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    }

    // ============================================================================
    // PATIENT LIST AND SEARCH
    // ============================================================================

    /**
     * Get comprehensive patient list with advanced filtering
     * @param {Object} params - Search and filter parameters
     * @returns {Promise<Object>} Patient list with metadata
     */
    async getPatientRecords(params = {}) {
        try {
            // Backend now automatically enforces role-based filtering for parents
            // No need for frontend role checking - security is handled server-side
            const searchParams = {
                page: params.page || 1,
                per_page: params.per_page || 50,
                search: params.search || '',
                status: params.status || '',
                service: params.service || '',
                assigned_to_me: params.assigned_to_me || false,
                sort_by: params.sort_by || 'updated_at',
                sort_order: params.sort_order || 'desc',
                student_class: params.student_class || '',
                student_section: params.student_section || ''
            };

            // Remove empty parameters
            Object.keys(searchParams).forEach(key => {
                if (!searchParams[key] && searchParams[key] !== false) {
                    delete searchParams[key];
                }
            });

            
            const response = await apiClient.get(this.baseUrl, { params: searchParams });
            
            
            // Handle different response formats
            let patients = [];
            let pagination = {
                total: 0,
                page: 1,
                per_page: 50,
                total_pages: 0,
                has_next: false,
                has_prev: false
            };

            if (response.data) {
                // Check if response has patients array directly or nested
                if (Array.isArray(response.data)) {
                    patients = response.data;
                    pagination.total = patients.length;
                    pagination.total_pages = 1;
                } else if (response.data.patients && Array.isArray(response.data.patients)) {
                    patients = response.data.patients;
                    pagination = {
                        total: response.data.total || patients.length,
                        page: response.data.page || 1,
                        per_page: response.data.per_page || 50,
                        total_pages: response.data.total_pages || Math.ceil((response.data.total || patients.length) / 50),
                        has_next: response.data.has_next || false,
                        has_prev: response.data.has_prev || false
                    };
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    patients = response.data.data;
                    pagination.total = response.data.total || patients.length;
                    pagination.total_pages = Math.ceil(pagination.total / 20);
                }
            }

            // Transform response for frontend compatibility
            const transformedData = {
                patients: patients,
                pagination: pagination,
                success: true,
                message: 'Patient records retrieved successfully'
            };

            return transformedData;

        } catch (error) {
            return {
                success: false,
                patients: [],
                pagination: { total: 0, page: 1, per_page: 50, total_pages: 0 },
                error: error.response?.data?.detail || 'Failed to fetch patient records'
            };
        }
    }

    /**
     * Advanced patient search with multiple criteria
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @returns {Promise<Object>} Search results
     */
    async searchPatients(query, options = {}) {
        try {
            const searchParams = {
                query: query.trim(),
                search_fields: options.fields || ['name', 'mrn', 'emirates_id'],
                limit: options.limit || 10
            };

            
            const response = await apiClient.get(`${this.baseUrl}/search`, { params: searchParams });
            
            return {
                success: true,
                patients: response.data.patients || [],
                total_results: response.data.total_results || 0,
                search_query: response.data.search_query || query,
                message: 'Search completed successfully'
            };

        } catch (error) {
            return {
                success: false,
                patients: [],
                total_results: 0,
                error: error.response?.data?.detail || 'Search failed'
            };
        }
    }

    /**
     * Get unique student class values for filtering
     * @returns {Promise<Object>} List of unique class values
     */
    async getStudentClasses() {
        try {
            const response = await apiClient.get(`${this.baseUrl}/student-classes`);

            if (response.data.success) {
                return {
                    success: true,
                    classes: response.data.classes || [],
                    total: response.data.total || 0
                };
            }

            return {
                success: false,
                classes: [],
                error: 'Failed to load student classes'
            };
        } catch (error) {
            console.error('Error fetching student classes:', error);
            return {
                success: false,
                classes: [],
                error: error.message || 'Failed to load student classes'
            };
        }
    }

    /**
     * Get unique student section values for filtering
     * @returns {Promise<Object>} List of unique section values
     */
    async getStudentSections() {
        try {
            const response = await apiClient.get(`${this.baseUrl}/student-sections`);

            if (response.data.success) {
                return {
                    success: true,
                    sections: response.data.sections || [],
                    total: response.data.total || 0
                };
            }

            return {
                success: false,
                sections: [],
                error: 'Failed to load student sections'
            };
        } catch (error) {
            console.error('Error fetching student sections:', error);
            return {
                success: false,
                sections: [],
                error: error.message || 'Failed to load student sections'
            };
        }
    }

    // ============================================================================
    // PATIENT DETAILS AND MEDICAL RECORDS
    // ============================================================================

    /**
     * Get comprehensive patient details by ID
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Complete patient information
     */
    async getPatientDetails(patientId) {
        try {
            // Check cache first
            const cacheKey = `patient_${patientId}`;
            const cached = this.cache.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
                return cached.data;
            }

            
            const response = await apiClient.get(`${this.baseUrl}/${patientId}`);
            
            const patientData = {
                success: true,
                patient: response.data,
                message: 'Patient details retrieved successfully'
            };

            // Cache the result
            this.cache.set(cacheKey, {
                data: patientData,
                timestamp: Date.now()
            });

            return patientData;

        } catch (error) {
            return {
                success: false,
                patient: null,
                error: error.response?.data?.detail || 'Failed to fetch patient details'
            };
        }
    }

    /**
     * Get patient by MRN (Medical Record Number)
     * @param {string} mrn - Medical Record Number
     * @returns {Promise<Object>} Patient information
     */
    async getPatientByMRN(mrn) {
        try {
            
            const response = await apiClient.get(`${this.baseUrl}/mrn/${mrn}`);
            
            return {
                success: true,
                patient: response.data,
                message: 'Patient found by MRN'
            };

        } catch (error) {
            return {
                success: false,
                patient: null,
                error: error.response?.data?.detail || 'Patient not found'
            };
        }
    }

    /**
     * Get patient medical history - using patient details endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Medical history data
     */
    async getPatientMedicalHistory(patientId, params = {}) {
        try {
            
            // Use the main patient endpoint since medical-history endpoint doesn't exist
            const response = await apiClient.get(`${this.baseUrl}/${patientId}`);
            
            return {
                success: true,
                medical_history: response.data.medical_record || {},
                message: 'Medical history retrieved successfully'
            };

        } catch (error) {
            return {
                success: false,
                medical_history: {},
                error: error.response?.data?.detail || 'Failed to fetch medical history'
            };
        }
    }

    /**
     * Add medical record entry - using patient update endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} recordData - Medical record data
     * @returns {Promise<Object>} Operation result
     */
    async addMedicalRecord(patientId, recordData) {
        try {
            
            // For now, we'll use the patient update endpoint
            // In a real implementation, you might want to add a dedicated medical record endpoint
            const response = await apiClient.put(`${this.baseUrl}/${patientId}`, {
                medical_record: recordData
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                record: response.data,
                message: 'Medical record added successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to add medical record'
            };
        }
    }

    // ============================================================================
    // PATIENT MEDICATIONS MANAGEMENT
    // ============================================================================

    /**
     * Get patient medications - using patient details endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Medications list
     */
    async getPatientMedications(patientId, params = {}) {
        try {
            
            // Use the main patient endpoint since medications endpoint doesn't exist
            const response = await apiClient.get(`${this.baseUrl}/${patientId}`);
            
            const medications = response.data.medical_record?.current_medications || [];
            
            return {
                success: true,
                medications: medications,
                total: medications.length,
                message: 'Medications retrieved successfully'
            };

        } catch (error) {
            return {
                success: false,
                medications: [],
                total: 0,
                error: error.response?.data?.detail || 'Failed to fetch medications'
            };
        }
    }

    /**
     * Add patient medication - using existing endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} medicationData - Medication data
     * @returns {Promise<Object>} Operation result
     */
    async addPatientMedication(patientId, medicationData) {
        try {
            
            // Use the existing medications endpoint from the backend
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/medications`, medicationData);
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                medication: response.data,
                message: 'Medication added successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to add medication'
            };
        }
    }

    // ============================================================================
    // PATIENT DOCUMENTS MANAGEMENT
    // ============================================================================

    /**
     * Get patient documents - using patient details endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Documents list
     */
    async getPatientDocuments(patientId, params = {}) {
        try {
            
            // Use the main patient endpoint since documents endpoint doesn't exist
            const response = await apiClient.get(`${this.baseUrl}/${patientId}`);
            
            const documents = response.data.documents || [];
            
            return {
                success: true,
                documents: documents,
                total_count: documents.length,
                message: 'Documents retrieved successfully'
            };

        } catch (error) {
            return {
                success: false,
                documents: [],
                total_count: 0,
                error: error.response?.data?.detail || 'Failed to fetch documents'
            };
        }
    }

    /**
     * Upload patient document - using correct documents endpoint
     * @param {string} patientId - Patient ID
     * @param {FormData} documentData - Document form data
     * @returns {Promise<Object>} Upload result
     */
    async uploadPatientDocument(patientId, documentData) {
        try {
            
            // Add patient_id to form data if not already present
            if (!documentData.get('patient_id')) {
                documentData.append('patient_id', patientId);
            }
            
            const response = await apiClient.post('/documents/upload', documentData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                document: response.data,
                message: 'Document uploaded successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to upload document'
            };
        }
    }

    // ============================================================================
    // PATIENT CLINICAL FORMS MANAGEMENT (WITH PAGINATION)
    // ============================================================================

    /**
     * Get paginated clinical forms for a specific patient
     * @param {string} patientId - Patient ID
     * @param {Object} params - Pagination and filter parameters
     * @returns {Promise<Object>} Paginated forms list
     */
    async getPatientClinicalForms(patientId, params = {}) {
        try {
            console.log('🎯 getPatientClinicalForms called with:', { patientId, params });

            // Prepare query parameters following the new endpoint specification
            const queryParams = {
                skip: params.skip || 0,
                limit: params.limit || 20,
                ...(params.search && { search: params.search }),
                ...(params.department_id && { department_id: params.department_id }),
                ...(params.category_id && { category_id: params.category_id }),
                ...(params.folder_id && { folder_id: params.folder_id })
            };

            console.log('🌐 API call params:', queryParams);

            // Make API call to the new paginated endpoint
            const response = await apiClient.get(`/dynamic-forms/forms`, { params: queryParams });

            console.log('📥 Raw API response:', response.data);

            // Process the response based on expected backend structure
            const responseData = response.data;

            return {
                success: true,
                items: responseData.items || responseData.data || [],
                total: responseData.total || responseData.total_count || 0,
                skip: responseData.skip || queryParams.skip,
                limit: responseData.limit || queryParams.limit,
                has_more: responseData.has_more || (responseData.items || responseData.data || []).length >= queryParams.limit,
                message: 'Clinical forms retrieved successfully'
            };

        } catch (error) {
            console.error('💥 Error in getPatientClinicalForms:', error);

            return {
                success: false,
                items: [],
                total: 0,
                skip: params.skip || 0,
                limit: params.limit || 20,
                has_more: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch patient clinical forms'
            };
        }
    }

    // ============================================================================
    // PATIENT APPOINTMENTS AND SCHEDULING
    // ============================================================================

    /**
     * Get patient appointments - gracefully handle missing endpoint
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Appointments list
     */
    async getPatientAppointments(patientId, params = {}) {
        try {
            
            // Return empty appointments since the endpoint doesn't exist
            // This prevents errors in the UI while maintaining functionality
            return {
                success: true,
                appointments: [],
                total: 0,
                message: 'Appointments feature not yet implemented'
            };

        } catch (error) {
            return {
                success: false,
                appointments: [],
                error: error.response?.data?.detail || 'Failed to fetch appointments'
            };
        }
    }

    // ============================================================================
    // PATIENT STATISTICS AND ANALYTICS
    // ============================================================================

    /**
     * Get patient statistics and analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Statistics data
     */
    async getPatientStatistics(params = {}) {
        try {
            
            const response = await apiClient.post(`${this.baseUrl}/getDashboardStats`, params);
            
            return {
                success: true,
                statistics: response.data.data || {},
                message: 'Statistics retrieved successfully'
            };

        } catch (error) {
            return {
                success: false,
                statistics: {},
                error: error.response?.data?.detail || 'Failed to fetch statistics'
            };
        }
    }

    // ============================================================================
    // PATIENT STATUS AND CARE MANAGEMENT
    // ============================================================================

    /**
     * Update patient status
     * @param {string} patientId - Patient ID
     * @param {Object|string} statusData - Status data object or status string (for backward compatibility)
     * @returns {Promise<Object>} Operation result
     */
    async updatePatientStatus(patientId, statusData) {
        try {
            // Handle backward compatibility - if statusData is a string, convert to object
            let requestData;
            if (typeof statusData === 'string') {
                requestData = { status: statusData };
            } else {
                requestData = {
                    status: statusData.status,
                    ...(statusData.discharge_date && { 
                        discharge_date: new Date(statusData.discharge_date).toISOString() 
                    }),
                    ...(statusData.discharge_time && { discharge_time: statusData.discharge_time }),
                    ...(statusData.discharge_reason && { discharge_reason: statusData.discharge_reason }),
                    ...(statusData.reason && { reason: statusData.reason })
                };
            }
            
            const response = await apiClient.put(`${this.baseUrl}/${patientId}/status`, requestData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                result: response.data,
                message: 'Patient status updated successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to update patient status'
            };
        }
    }

    /**
     * Discharge patient from facility
     * @param {string} patientId - Patient ID
     * @param {Object} dischargeData - Discharge information
     * @returns {Promise<Object>} Operation result
     */
    async dischargePatient(patientId, dischargeData) {
        try {
            
            // Use the existing status update endpoint instead of non-existent discharge endpoint
            const response = await apiClient.put(`${this.baseUrl}/${patientId}/status`, "discharged", {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                result: response.data,
                message: 'Patient discharged successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to discharge patient'
            };
        }
    }

    /**
     * Transfer patient to different location/service
     * @param {string} patientId - Patient ID
     * @param {Object} transferData - Transfer information
     * @returns {Promise<Object>} Operation result
     */
    async transferPatient(patientId, transferData) {
        try {
            
            // Use the existing status update endpoint instead of non-existent transfer endpoint
            const response = await apiClient.put(`${this.baseUrl}/${patientId}/status`, "transferred", {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                result: response.data,
                message: 'Patient transferred successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to transfer patient'
            };
        }
    }

    /**
     * Update patient location
     * @param {string} patientId - Patient ID
     * @param {Object} locationData - Location information
     * @returns {Promise<Object>} Operation result
     */
    async updatePatientLocation(patientId, locationData) {
        try {
            
            // Use the existing patient update endpoint for location updates
            const response = await apiClient.put(`${this.baseUrl}/${patientId}`, {
                location_notes: JSON.stringify(locationData)
            });
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                result: response.data,
                message: 'Patient location updated successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to update patient location'
            };
        }
    }

    // ============================================================================
    // PATIENT INFORMATION MANAGEMENT
    // ============================================================================

    /**
     * Update patient information
     * @param {string} patientId - Patient ID
     * @param {Object} patientData - Patient data to update
     * @returns {Promise<Object>} Operation result
     */
    async updatePatient(patientId, patientData) {
        try {
            
            const response = await apiClient.put(`${this.baseUrl}/${patientId}`, patientData);
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                patient: response.data,
                message: 'Patient information updated successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to update patient'
            };
        }
    }

    /**
     * Assign staff to patient
     * @param {string} patientId - Patient ID
     * @param {Object} staffData - Staff assignment data
     * @returns {Promise<Object>} Operation result
     */
    async assignStaffToPatient(patientId, staffData) {
        try {
            
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/staff-assignments`, staffData);
            
            // Clear cache for this patient
            this.clearPatientCache(patientId);
            
            return {
                success: true,
                result: response.data,
                message: 'Staff assigned successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to assign staff'
            };
        }
    }

    /**
     * Validate custom MRN suffix availability and format
     * @param {Object} data - MRN validation data with mrn_suffix
     * @returns {Promise<Object>} Validation result
     */
    async validateCustomMRN(data) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/validate-mrn`, data);
            
            return {
                success: true,
                data: response.data,
                message: 'MRN validation successful'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to validate MRN'
            };
        }
    }

    /**
     * Complete patient registration
     * @param {Object} registrationData - Registration data
     * @returns {Promise<Object>} Operation result
     */
    async completePatientRegistration(registrationData) {
        try {
            
            const response = await apiClient.post(`${this.baseUrl}/register`, registrationData);
            
            return {
                success: true,
                patient: response.data,
                message: 'Patient registration completed successfully'
            };

        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to complete registration'
            };
        }
    }

    // ============================================================================
    // CACHE MANAGEMENT
    // ============================================================================

    /**
     * Clear cache for specific patient
     * @param {string} patientId - Patient ID
     */
    clearPatientCache(patientId) {
        const cacheKey = `patient_${patientId}`;
        this.cache.delete(cacheKey);
    }

    /**
     * Clear all cache
     */
    clearAllCache() {
        this.cache.clear();
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Format patient data for display
     * @param {Object} patient - Patient data
     * @returns {Object} Formatted patient data
     */
    formatPatientForDisplay(patient) {
        if (!patient) return null;

        // Extract nested objects
        const demographics = patient.demographics || {};
        const medicalRecord = patient.medical_record || {};
        const careStatus = patient.care_status || {};
        const address = demographics.address || {};

        // Helper function to get first non-null value
        const getFirstValue = (...values) => values.find(v => v !== undefined && v !== null);

        return {
            id: patient.id,
            mrn: getFirstValue(
                patient.current_mrn,
                patient.mrn_permanent,
                patient.mrn_temporary,
                patient.mrn
            ),
            full_name: getFirstValue(
                patient.full_name,
                demographics.full_name,
                `${demographics.first_name || ''} ${demographics.last_name || ''}`.trim()
            ),
            first_name: demographics.first_name,
            last_name: demographics.last_name,
            middle_name: demographics.middle_name,
            date_of_birth: demographics.date_of_birth,
            age: getFirstValue(demographics.age, patient.age),
            gender: getFirstValue(demographics.gender, patient.gender),
            nationality: getFirstValue(demographics.nationality, patient.nationality),
            phone: getFirstValue(demographics.phone_primary, demographics.phone, patient.phone),
            phone_secondary: demographics.phone_secondary,
            email: getFirstValue(demographics.email, patient.email),
            emirates_id: getFirstValue(patient.emirates_id, demographics.emirates_id),
            passport_number: patient.passport_number,
            address: {
                street: address.street,
                city: address.city,
                emirate: address.emirate,
                country: address.country,
                postal_code: address.postal_code
            },
            emergency_contacts: demographics.emergency_contacts || [],
            blood_group: getFirstValue(medicalRecord.blood_group, patient.blood_group),
            height: getFirstValue(medicalRecord.height, patient.height),
            weight: getFirstValue(medicalRecord.weight, patient.weight),
            bmi: getFirstValue(medicalRecord.bmi, patient.bmi),
            allergies: medicalRecord.allergies || [],
            medical_conditions: medicalRecord.medical_conditions || [],
            current_medications: medicalRecord.current_medications || [],
            insurance_info: medicalRecord.insurance_info || [],
            status: getFirstValue(careStatus.status, patient.status),
            current_services: getFirstValue(careStatus.current_services, patient.current_services, []),
            admission_date: getFirstValue(careStatus.admission_date, patient.admission_date),
            discharge_date: getFirstValue(careStatus.discharge_date, patient.discharge_date),
            last_visit: getFirstValue(careStatus.last_visit_date, patient.last_visit),
            next_appointment: getFirstValue(careStatus.next_appointment_date, patient.next_appointment),
            primary_nurse: getFirstValue(careStatus.primary_nurse, patient.primary_nurse),
            attending_physician: getFirstValue(careStatus.attending_physician, patient.attending_physician),
            current_location: careStatus.current_location,
            location_notes: careStatus.location_notes,
            location_updated_at: careStatus.location_updated_at,
            registration_status: getFirstValue(patient.registration_status, 'pending'),
            is_registered: getFirstValue(patient.is_registered, false),
            document_count: getFirstValue(patient.document_count, 0),
            created_at: patient.created_at,
            updated_at: patient.updated_at,
            created_by: patient.created_by
        };
    }

    /**
     * Get patient status badge configuration
     * @param {string} status - Patient status
     * @returns {Object} Badge configuration
     */
    getStatusBadgeConfig(status) {
        const configs = {
            'active': { color: 'success', label: 'Active', icon: '✅' },
            'discharged': { color: 'secondary', label: 'Discharged', icon: '🏠' },
            'transferred': { color: 'warning', label: 'Transferred', icon: '🔄' },
            'deceased': { color: 'dark', label: 'Deceased', icon: '💔' },
            'on_hold': { color: 'info', label: 'On Hold', icon: '⏸️' },
            'registered': { color: 'success', label: 'Registered', icon: '✅' },
            'pending': { color: 'warning', label: 'Pending', icon: '⏳' },
            'pre_registered': { color: 'info', label: 'Pre-registered', icon: '📝' }
        };
        
        return configs[status] || { color: 'light', label: 'Unknown', icon: '❓' };
    }

    /**
     * Get available patient status options
     * @returns {Array} Array of status options
     */
    getStatusOptions() {
        return [
            { value: 'active', label: 'Active', color: 'success' },
            { value: 'discharged', label: 'Discharged', color: 'secondary' },
            { value: 'transferred', label: 'Transferred', color: 'warning' },
            { value: 'on_hold', label: 'On Hold', color: 'info' },
            { value: 'deceased', label: 'Deceased', color: 'dark' }
        ];
    }
}

// Export singleton instance
const patientRecordsService = new PatientRecordsService();
export default patientRecordsService;